import type {
  ProposalCollectionItemRevertOperation,
  ProposalFieldSetOperation,
  ProposalTimelineEntry,
} from "work/proposals/negotiation/proposal-collaboration-contract";

type IdFactory = () => string;

/**
 * Builds the well-formed collection operation a collection-item history revert must queue.
 *
 * `ProposalFieldTimelineDropdown` offers "revert to a previous value" both for scalar fields (routed
 * back through the field's own builder setter, so it lands as a `FieldSet`/resolution op the same way
 * a manual edit would) and, per item, for collection fields (fee-schedule categories, documents, team
 * members). A collection item has no whole-field setter to route a revert through, so this builds
 * exactly the operation kind `buildCollectionOperations` (proposal-command-builder.ts) would have
 * produced for the equivalent hand-edit: `CollectionItemUpdated` when the item currently exists,
 * `CollectionItemAdded` when the most recent event for the item was its removal (there is nothing to
 * update — the revert restores it). Never a raw whole-field `FieldSet`: that operation kind belongs to
 * the scalar path list only, and sending a collection path through it would misroute the change and
 * (per ROADMAP.md item 3) risk silently promoting the field to `ProposeAlternative`.
 */
export function buildProposalCollectionItemRevertOperation(args: {
  path: string;
  itemId: string;
  /** The item's most recently known state — its current value, or absence if it is currently removed. */
  currentEntry: Pick<ProposalTimelineEntry, "valueExists" | "value">;
  /** The historical state being reverted to. */
  targetEntry: Pick<ProposalTimelineEntry, "valueExists" | "value">;
  createId?: IdFactory;
}): ProposalCollectionItemRevertOperation | undefined {
  const { path, itemId, currentEntry, targetEntry, createId = () => crypto.randomUUID() } = args;
  // Nothing displayable to revert to (a removal entry carries no value) — the dropdown already
  // excludes these, but stay defensive since this is a public builder other callers may reach.
  if (!targetEntry.valueExists) return undefined;

  const conflictKey = collectionItemConflictKey(path, itemId);
  const value = structuredClone(targetEntry.value) as never;

  if (!currentEntry.valueExists) {
    return {
      operationId: createId(),
      kind: "CollectionItemAdded",
      path,
      conflictKey,
      itemId,
      value,
    };
  }

  return {
    operationId: createId(),
    kind: "CollectionItemUpdated",
    path,
    conflictKey,
    itemId,
    expectedValue: structuredClone(currentEntry.value) as never,
    value,
  };
}

/** Mirrors `collectionConflictKey` in proposal-command-builder.ts for a whole-item (no leaf) target. */
function collectionItemConflictKey(path: string, itemId: string, aspect?: string): string {
  return [path.slice(1).replaceAll("/", ":"), itemId, ...(aspect ? [aspect] : [])].join(":");
}

/**
 * One column of a collection item, as a history menu offers it: the canonical property the negotiation
 * stream keys on, and what to call it.
 */
export type ProposalCollectionItemAspect = {
  /** The conflict key's leaf — `feeRate`, `type`, … */
  key: string;
  label: string;
};

/**
 * How a value reads when it is not something the reader can show.
 *
 * A fee category's team member is two ids and a name that lives elsewhere; a column this build has never
 * heard of could be anything. Printing an id would be worse than useless and — for a column the server
 * masks — a leak, while printing nothing hides that anything moved. So the row says exactly what is
 * known: this changed.
 *
 * Deliberately not `proposalEmptyValueText`. "[Empty]" is a claim about the value — that there isn't
 * one — and a team member who has been reassigned is not an absence.
 */
export const proposalCollectionItemUnreadableValueText = "(changed)";

/**
 * A conflict key's leaf as a heading: `teamMember` → "Team Member".
 *
 * For a column no caller declared. A collection's known columns are named by whoever declares them, but
 * the stream is free to carry a leaf this build has never heard of — a property added server-side, or
 * one a surface chose not to list — and such a column still has a history somebody may need to take
 * back. Deriving a heading from the key is what lets it be offered at all; the alternative is a section
 * with no name, or no section, which is how a fee category's team member became unrevertible.
 */
export function proposalCollectionItemAspectLabel(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_:.-]+/g, " ")
    .trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

/**
 * Builds the operation a single-COLUMN revert must queue — the per-leaf counterpart of
 * {@link buildProposalCollectionItemRevertOperation}.
 *
 * A fee schedule is negotiated one column at a time (#4847): the command builder diffs a category leaf
 * by leaf, so a rate and a type changed in the same save are two operations, two conflicts and
 * two histories. Reverting has to work at the same grain, or taking back a rate would also take back
 * every other column that moved since — which is what a whole-item revert does. This produces exactly
 * the operation `buildCollectionOperations` would have produced for the equivalent hand-edit of that one
 * column: `CollectionItemUpdated`, the conflict key carrying the leaf, and both values wrapped in the
 * column's own name (the "expectedValue must use the same nested leaf shape as value" rule the server
 * enforces in `ProposalCollectionValuePolicy`).
 */
export function buildProposalCollectionItemAspectRevertOperation(args: {
  path: string;
  itemId: string;
  /** The column being reverted — the conflict key's leaf. */
  aspect: string;
  /** What the column holds now. `null` is a column explicitly cleared, which is a value, not an absence. */
  currentValue: unknown;
  /** What it held at the point being reverted to. */
  targetValue: unknown;
  createId?: IdFactory;
}): ProposalCollectionItemRevertOperation {
  const { path, itemId, aspect, currentValue, targetValue, createId = () => crypto.randomUUID() } = args;

  return {
    operationId: createId(),
    kind: "CollectionItemUpdated",
    path,
    conflictKey: collectionItemConflictKey(path, itemId, aspect),
    itemId,
    expectedValue: { [aspect]: structuredClone(currentValue) } as never,
    value: { [aspect]: structuredClone(targetValue) } as never,
  };
}

/**
 * The column a built revert operation addresses, or undefined when it restores the whole item.
 *
 * A caller applying a revert has to know which of the two it was handed: a whole-item value replaces the
 * item, a single column's value has to be merged into the item that is there. Reading it back off the
 * conflict key keeps that decision on the operation itself rather than on the call site remembering
 * which affordance the user clicked.
 */
export function proposalCollectionItemRevertAspect(
  operation: ProposalCollectionItemRevertOperation | undefined,
): string | undefined {
  if (!operation) return undefined;
  return proposalCollectionItemConflictKeyAspect(operation.path, operation.itemId, operation.conflictKey);
}

/**
 * The column a conflict key addresses, or undefined when it addresses the item itself.
 *
 * The same question {@link proposalCollectionItemRevertAspect} asks of a built operation, asked of a
 * timeline entry: `…:{itemId}` is the item arriving or leaving, `…:{itemId}:feeRate` is one of its
 * columns. Shared so "is this about the whole item?" has one answer rather than one per call site.
 *
 * Which of the two ways it reads a key depends on whether it was given a path, and the two are
 * alternatives rather than a first choice and a retry:
 *
 * - **With a path** the key must belong to *this* collection and *this* item, matched against the key
 *   `collectionItemConflictKey` would have built. Anything else answers undefined — including a
 *   well-formed key from a different collection, which is the whole point of checking: reading
 *   `feeSchedule:categories:{id}:feeRate` as a column of `/vendorTeam/members` would offer one
 *   collection's history as another's.
 * - **Without a path** there is nothing to match against, so the column is read by position
 *   (`…:{itemId}:{aspect}`). A caller reaches this by having no path to give: the history dropdown
 *   passes `entry.path ?? path ?? ""`, and a legacy entry carrying neither leaves it empty — for which
 *   `collectionItemConflictKey("", itemId)` yields a malformed `":{itemId}"` that could never match,
 *   so a strict-only reading would answer undefined for every column of such an entry.
 *
 * Letting the positional reading run *after* a failed prefix match instead of *instead of* one is what
 * makes the first bullet stop holding: the mismatched key from another collection falls through to
 * position and resolves after all.
 */
export function proposalCollectionItemConflictKeyAspect(
  path: string | null | undefined,
  itemId: string,
  conflictKey: string | null | undefined,
): string | undefined {
  if (!conflictKey) return undefined;

  // A path is something to validate against, so validate: this collection and this item, or nothing.
  if (path) {
    const itemKey = collectionItemConflictKey(path, itemId);
    if (!conflictKey.startsWith(`${itemKey}:`)) return undefined;
    return conflictKey.slice(itemKey.length + 1) || undefined;
  }

  // No path, nothing to validate against: read the column by position, `...:{itemId}:{aspect}`.
  const parts = conflictKey.split(":");
  return parts.at(-2) === itemId ? parts.at(-1) || undefined : undefined;
}

/**
 * The collection path a conflict key was built from — the inverse of {@link collectionItemConflictKey}.
 *
 * A timeline entry's `path` is optional on the wire, and an operation built without one is not merely
 * unlabelled but wrong: it would be queued against whatever path the caller substituted. The key itself
 * carries the answer, because it was built from the path — `feeSchedule:categories:{itemId}:feeRate` was
 * built from `/feeSchedule/categories` — so the segments before the item are the collection it lives in.
 *
 * Answers undefined when the key does not name the item, or names it with no collection in front of it.
 * A caller that cannot establish a path must offer no revert rather than guess one: there is no such
 * thing as a harmlessly mis-addressed operation.
 */
export function proposalCollectionPathFromConflictKey(
  conflictKey: string | null | undefined,
  itemId: string,
): string | undefined {
  if (!conflictKey || !itemId) return undefined;
  const segments = conflictKey.split(":");
  // Case-insensitively, the way every other identity comparison in the negotiation does it: an id's
  // casing depends on which layer serialized it.
  const itemIndex = segments.findIndex((segment) => segment.toLowerCase() === itemId.toLowerCase());
  if (itemIndex < 1) return undefined;
  return `/${segments.slice(0, itemIndex).join("/")}`;
}

/**
 * What one column of a collection item held, according to a timeline entry — if that entry says anything
 * about the column at all.
 *
 * Two payload shapes state a column, and a per-column history has to read both: the column's own leaf
 * event (`{ "feeRate": 125 }`, keyed `…:{itemId}:feeRate`) and a whole-item event — an add, a removal,
 * a creation baseline — which states every column at once. Existence is distinct from emptiness: a
 * column an event does not mention is not a revert target, while one it explicitly cleared is.
 */
export function readProposalCollectionItemAspectValue(
  conflictKey: string | null | undefined,
  payload: unknown,
  aspect: string,
): { exists: boolean; value: unknown } {
  if (isRecord(payload)) {
    return Object.prototype.hasOwnProperty.call(payload, aspect)
      ? { exists: true, value: payload[aspect] }
      : { exists: false, value: undefined };
  }
  // A bare scalar states the column its own conflict key names, and nothing else.
  const leaf = conflictKey?.split(":").at(-1);
  if (leaf === aspect && payload !== undefined) return { exists: true, value: payload };
  return { exists: false, value: undefined };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Builds the well-formed `FieldSet` operation a *structured* scalar field's history revert must queue —
 * the `FieldSet` counterpart of {@link buildProposalCollectionItemRevertOperation} above, for a scalar
 * path whose value is a JSON object (e.g. `/vendorTeam/leader`) rather than a plain string.
 *
 * A plain-text scalar (Name, Description, a date) reverts by feeding its `nextValue` — the stored value,
 * not the timeline row's rendering of it — back through the field's own builder setter, letting the
 * normal edit-diff pipeline build the real operation fresh; there is no scalar value for a structured
 * one to route that way. Like the collection-item
 * revert above, no caller is expected to queue this operation's `expectedValue`/`conflictKey` as-is;
 * {@link proposalScalarFieldRevertValue} extracts just the reverted-to `value` so the caller can re-apply
 * it through the field's own setter (e.g. `Proposal.updateVendorTeamLeader`), the same way a collection
 * item's revert re-applies through `replaceEntryById`. This keeps `queueProposalUpdate` the one place an
 * operation is actually built for the wire, with a fresh (non-stale) `expectedValue` — never a raw
 * whole-team overwrite that would also touch sibling data (e.g. `/vendorTeam/members`).
 */
export function buildProposalScalarFieldRevertOperation(args: {
  path: string;
  /** The field's most recently known state — its current value, or absence if currently unset. */
  currentEntry: Pick<ProposalTimelineEntry, "valueExists" | "value">;
  /** The historical state being reverted to. */
  targetEntry: Pick<ProposalTimelineEntry, "valueExists" | "value">;
  createId?: IdFactory;
}): ProposalFieldSetOperation | undefined {
  const { path, currentEntry, targetEntry, createId = () => crypto.randomUUID() } = args;
  // Nothing displayable to revert to (a removal/unset entry carries no value) — the dropdown already
  // excludes these, but stay defensive since this is a public builder other callers may reach.
  if (!targetEntry.valueExists) return undefined;

  return {
    operationId: createId(),
    kind: "FieldSet",
    path,
    conflictKey: path.slice(1).replaceAll("/", ":"),
    expectedValue: currentEntry.valueExists ? (structuredClone(currentEntry.value) as never) : undefined,
    value: structuredClone(targetEntry.value) as never,
  };
}

/**
 * Extracts the reverted-to value from a structured scalar's {@link ProposalFieldTimelineRevertEntry}
 * `operation` — the `FieldSet` counterpart of {@link proposalCollectionItemRevertValue}. The parameter
 * type is {@link ProposalFieldSetOperation}, not the full `ProposalContentOperation` union, so a call
 * site can no longer hand this a `CollectionItemAdded`/`CollectionItemUpdated` (or anything else) and
 * have it silently do nothing — that mismatch is now a compile error, not a runtime `undefined`.
 */
export function proposalScalarFieldRevertValue(operation: ProposalFieldSetOperation | undefined): unknown {
  return operation?.value;
}

/**
 * Extracts the reverted-to item's raw JSON value from a {@link ProposalFieldTimelineRevertEntry}'s
 * `operation`, for callers that must parse it back into their own domain type (via that type's own
 * `fromJSON`) before applying the revert through their normal edit path.
 *
 * The parameter type is {@link ProposalCollectionItemRevertOperation} — just `CollectionItemAdded` /
 * `CollectionItemUpdated`, the two kinds {@link buildProposalCollectionItemRevertOperation} can produce —
 * rather than the full `ProposalContentOperation` union. A call site can no longer pass a `FieldSet`
 * (ROADMAP.md's "a revert is never a raw FieldSet", the other direction), a `CollectionItemRemoved`, or a
 * `ResolutionDecision` and have this silently return `undefined`; the mismatch fails to compile instead.
 */
export function proposalCollectionItemRevertValue(
  operation: ProposalCollectionItemRevertOperation | undefined,
): unknown {
  return operation?.value;
}
